import React, { useMemo, useState } from 'react'
import {
  CVRParticipant,
  FoundingParentManagementSnapshot,
} from '../../types'
import { participantRowKey } from '../../utils/participantKey'
import { formatDate, normalizeCvr } from '../../utils/format'
import BeneficialOwners from './BeneficialOwners'
import LegalOwners from './LegalOwners'
import ParticipantRow, { StripExpandLink, StripHistoricDivider, StripSectionHeader } from './ParticipantRow'

interface CompanyOwnershipStripProps {
  beneficialOwners: CVRParticipant[]
  legalOwners: CVRParticipant[]
  founders: CVRParticipant[]
  foundingManagementAtParents?: FoundingParentManagementSnapshot[]
  startdato?: string | null
}

function stripCountLabel(title: string, count: number): string {
  return count > 0 ? `${title} (${count})` : title
}

export function snapshotMatchesFounder(
  founder: CVRParticipant,
  snap: FoundingParentManagementSnapshot,
): boolean {
  const founderCvr = normalizeCvr(founder.cvrNummer)
  const snapCvr = normalizeCvr(snap.parentCvr)
  if (founder.enhedstype === 'VIRKSOMHED' && founderCvr.length === 8 && founderCvr === snapCvr) {
    return true
  }
  const founderName = founder.navn?.trim().toLowerCase()
  const snapName = snap.parentNavn?.trim().toLowerCase()
  return !!founderName && founderName === snapName
}

function snapshotForFounder(
  founder: CVRParticipant,
  snapshots: FoundingParentManagementSnapshot[],
): FoundingParentManagementSnapshot | undefined {
  return snapshots.find((snap) => snapshotMatchesFounder(founder, snap))
}

const InvolvedPeopleList: React.FC<{
  snap: FoundingParentManagementSnapshot
}> = ({ snap }) => (
  <ul className="list-none space-y-0.5 mt-0.5">
    {snap.people.map((person, j) => (
      <ParticipantRow
        key={participantRowKey(`strip-parent-mgmt-${snap.parentCvr}`, person, j)}
        participant={person}
        variant="strip"
        forceHistoric
        stripExpanded
      />
    ))}
  </ul>
)

const FoundingColumn: React.FC<{
  founders: CVRParticipant[]
  foundingManagementAtParents: FoundingParentManagementSnapshot[]
  startdato?: string | null
}> = ({ founders, foundingManagementAtParents, startdato }) => {
  const [showExpanded, setShowExpanded] = useState(false)

  const active = founders.filter((p) => p.role.isActive)
  const historic = founders.filter((p) => !p.role.isActive)
  const dateLabel = formatDate(startdato)
  const title = stripCountLabel('Stiftere', active.length)

  const hasInvolved = foundingManagementAtParents.length > 0
  const hasHistoric = historic.length > 0
  const hasExpand = hasInvolved || hasHistoric

  const orphanSnapshots = useMemo(
    () =>
      foundingManagementAtParents.filter(
        (snap) => !active.some((founder) => snapshotMatchesFounder(founder, snap)),
      ),
    [active, foundingManagementAtParents],
  )

  const expandLabel = useMemo(() => {
    if (showExpanded) {
      return hasInvolved ? 'Skjul' : `Skjul historiske (${historic.length})`
    }
    if (hasInvolved) return 'Vis hvem'
    return `Vis historiske (${historic.length})`
  }, [showExpanded, hasInvolved, historic.length])

  const showHistoricBlock = showExpanded && hasHistoric
  const showInvolvedBlock = showExpanded && hasInvolved
  const hasPrimaryRows = active.length > 0 || (showInvolvedBlock && orphanSnapshots.length > 0)

  const expandToggle = hasExpand ? (
    <StripExpandLink
      expanded={showExpanded}
      onToggle={() => setShowExpanded((v) => !v)}
    >
      {expandLabel}
    </StripExpandLink>
  ) : null

  return (
    <div className="min-w-0">
      <StripSectionHeader
        title={title}
        suffix={dateLabel && active.length > 0 ? dateLabel : null}
        action={expandToggle}
      />

      {active.length === 0 &&
        foundingManagementAtParents.length === 0 &&
        historic.length === 0 && (
          <p className="text-xs text-ink-500">Ingen registrerede stiftere.</p>
        )}

      {active.length > 0 && (
        <ul className="list-none space-y-0.5">
          {active.map((p, i) => {
            const snap = showInvolvedBlock ? snapshotForFounder(p, foundingManagementAtParents) : undefined

            if (!snap) {
              return (
                <ParticipantRow
                  key={participantRowKey('strip-founding-a', p, i)}
                  participant={p}
                  variant="strip"
                />
              )
            }

            return (
              <li
                key={participantRowKey('strip-founding-a', p, i)}
                className="list-none py-0.5"
              >
                <ParticipantRow participant={p} variant="strip" stripInline />
                <InvolvedPeopleList snap={snap} />
              </li>
            )
          })}
        </ul>
      )}

      {showInvolvedBlock &&
        orphanSnapshots.map((snap) => (
          <div key={`orphan-founding-${snap.parentCvr}`} className={active.length > 0 ? 'mt-0.5' : ''}>
            <ParticipantRow
              participant={{
                enhedsNummer: 0,
                enhedstype: 'VIRKSOMHED',
                cvrNummer: snap.parentCvr,
                navn: snap.parentNavn,
                navnNu: snap.parentNavnNu,
                adresse: '',
                isAddressConfidential: false,
                isAddressUpdateStopped: false,
                lastUpdated: null,
                lastLoaded: null,
                role: {
                  organizationType: 'Stifter',
                  function: 'Stifter',
                  validFrom: null,
                  validTo: null,
                  isActive: true,
                  ownershipCapitalPercent: null,
                  ownershipVotingPercent: null,
                },
              }}
              variant="strip"
              stripInline
            />
            <InvolvedPeopleList snap={snap} />
          </div>
        ))}

      {showHistoricBlock && (
        <>
          {hasPrimaryRows && <StripHistoricDivider />}
          <ul className="list-none space-y-0.5">
            {historic.map((p, i) => (
              <ParticipantRow
                key={participantRowKey('strip-founding-h', p, i)}
                participant={p}
                variant="strip"
                forceHistoric
                stripExpanded
              />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * Compact three-column ownership summary for the company Ejerskab tab —
 * mirrors `PersonOverviewStrip` on the person profile.
 */
const CompanyOwnershipStrip: React.FC<CompanyOwnershipStripProps> = ({
  beneficialOwners,
  legalOwners,
  founders,
  foundingManagementAtParents = [],
  startdato = null,
}) => {
  const boActive = beneficialOwners.filter((p) => p.role.isActive).length
  const legalPrimary = legalOwners.filter(
    (p) => p.role.legalRegisterShowInPrimaryList === true,
  ).length

  const columnClass = 'py-3 px-4 sm:px-5 min-w-0'

  return (
    <div
      data-testid="company-ownership-strip"
      className="border border-rule rounded-sm overflow-hidden grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-rule text-xs"
    >
      <div className={`${columnClass} md:border-l-2 md:border-l-signal`}>
        <BeneficialOwners
          owners={beneficialOwners}
          embedded
          layout="overview"
          stripHeader={{ title: stripCountLabel('Reelle ejere', boActive) }}
        />
      </div>

      <div className={columnClass}>
        <LegalOwners
          owners={legalOwners}
          embedded
          layout="overview"
          stripHeader={{ title: stripCountLabel('Ejere', legalPrimary) }}
        />
      </div>

      <div className={columnClass}>
        <FoundingColumn
          founders={founders}
          foundingManagementAtParents={foundingManagementAtParents}
          startdato={startdato}
        />
      </div>
    </div>
  )
}

export default CompanyOwnershipStrip
